"use client";

import { useEffect, useId, useState } from "react";

import { CalendarIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldContent, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

const uiText = {
  placeholders: {
    birthDate: "aaaa-mm-jj",
  },
  labels: {
    address: "Adresse *",
    birthDate: "Date de naissance *",
    city: "Ville *",
    email: "Email *",
    firstName: "Prénom *",
    lastName: "Nom *",
    phone: "Téléphone *",
    postalCode: "Code postal *",
  },
};

type RetailIndividualCreateValues = {
  addressLine: string;
  birthDate: string;
  city: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  postalCode: string;
};

const defaultValues: RetailIndividualCreateValues = {
  addressLine: "",
  birthDate: "",
  city: "",
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  postalCode: "",
};

function RetailIndividualCreateForm({
  onCanProceedChange,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const idPrefix = useId();
  const [values, setValues] =
    useState<RetailIndividualCreateValues>(defaultValues);

  const canProceed = Boolean(
    values.firstName.trim() &&
    values.lastName.trim() &&
    values.birthDate.trim() &&
    values.addressLine.trim() &&
    values.city.trim() &&
    values.postalCode.trim() &&
    values.email.trim() &&
    values.phone.trim(),
  );

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card className="border-muted">
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor={`${idPrefix}-email`}>
              {uiText.labels.email}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-email`}
                required
                type="email"
                value={values.email}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </FieldContent>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${idPrefix}-phone`}>
              {uiText.labels.phone}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-phone`}
                required
                value={values.phone}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel htmlFor={`${idPrefix}-lastName`}>
              {uiText.labels.lastName}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-lastName`}
                required
                value={values.lastName}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, lastName: e.target.value }))
                }
              />
            </FieldContent>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${idPrefix}-firstName`}>
              {uiText.labels.firstName}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-firstName`}
                required
                value={values.firstName}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, firstName: e.target.value }))
                }
              />
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel htmlFor={`${idPrefix}-birthDate`}>
              {uiText.labels.birthDate}
            </FieldLabel>
            <FieldContent>
              <InputGroup>
                <InputGroupInput
                  id={`${idPrefix}-birthDate`}
                  placeholder={uiText.placeholders.birthDate}
                  required
                  value={values.birthDate}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      birthDate: e.target.value,
                    }))
                  }
                />
                <InputGroupAddon align="inline-end">
                  <CalendarIcon className="text-muted-foreground" />
                </InputGroupAddon>
              </InputGroup>
            </FieldContent>
          </Field>

          <Field className="md:col-span-2">
            <FieldLabel htmlFor={`${idPrefix}-addressLine`}>
              {uiText.labels.address}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-addressLine`}
                required
                value={values.addressLine}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    addressLine: e.target.value,
                  }))
                }
              />
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel htmlFor={`${idPrefix}-postalCode`}>
              {uiText.labels.postalCode}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-postalCode`}
                required
                value={values.postalCode}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    postalCode: e.target.value,
                  }))
                }
              />
            </FieldContent>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${idPrefix}-city`}>
              {uiText.labels.city}
            </FieldLabel>
            <FieldContent>
              <Input
                id={`${idPrefix}-city`}
                required
                value={values.city}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, city: e.target.value }))
                }
              />
            </FieldContent>
          </Field>
        </div>
      </CardContent>
    </Card>
  );
}

export type { RetailIndividualCreateValues };
export { RetailIndividualCreateForm };
